import React, { useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress, InputAdornment, MenuItem, FormControlLabel, Switch } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationUsuarioSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import usuarioService from "../../../services/usuarioService.ts";
import { Usuario } from "../../../models/Usuario.ts";
import { formatBrazilPhone } from "../../../utils/phone.util.ts";
import PermissoesPreview from "../../PermissoesPreview/index.tsx";
import { usePermissoes } from "../../../contexts/PermissoesContext.tsx";
import { TipoUsuario } from "../../../types/Permissoes.ts";

interface CreateUsuarioDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

// Mesma receita de cores dos demais módulos — sem ThemeProvider do MUI,
// estados de label/borda caem no tema claro padrão do MUI, sem contraste
// no fundo escuro. CSS vars do design system em vez de tokens de tema.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const CreateUsuarioDrawer: React.FC<CreateUsuarioDrawerProps> = ({ open, onClose, onCreated }) => {
  const { showFeedback } = useFeedback();
  const { catalogo } = usePermissoes();
  const [isLoading, setIsLoading] = useState(false);
  const [showSenha, setShowSenha] = useState(false);

  // OPERADOR como padrao: e a funcao do dia a dia, e errar para o lado
  // restrito e recuperavel (o ADM promove depois). Errar para ADM entregaria
  // controle da conta inteira sem ninguem ter pedido.
  const [tipo, setTipo] = useState<TipoUsuario>("OPERADOR");
  const [podeExcluirAgendamento, setPodeExcluirAgendamento] = useState(false);
  const [podeVerLog, setPodeVerLog] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationUsuarioSchema),
  });

  const handleClose = () => {
    reset({});
    setTipo("OPERADOR");
    setPodeExcluirAgendamento(false);
    setPodeVerLog(false);
    onClose();
  };

  const onSubmit = async (data: any) => {
    // O model Usuario descarta campos que nao conhece, entao funcao e
    // permissoes vao por fora dele.
    const novoUsuario = Object.assign(
      new Usuario({ email: data.email, senha: data.senha, telefone: data.telefone }),
      { nome: data.nome || undefined, tipo, podeExcluirAgendamento, podeVerLog }
    );

    try {
      setIsLoading(true);
      // POST /usuario não retorna o usuário criado (só uma mensagem de texto)
      // — diferente dos outros módulos, então quem chama precisa refazer o
      // fetch da lista em vez de anexar response.data.
      await usuarioService.create(novoUsuario);
      showFeedback("Usuário cadastrado com sucesso!", "success");
      onCreated?.();
      reset({});
      setTipo("OPERADOR");
      setPodeExcluirAgendamento(false);
      setPodeVerLog(false);
    } catch (err: any) {
      showFeedback(err.message || "Erro ao cadastrar usuário", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <div>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Novo Usuário</Typography>
          <Typography variant="body2" sx={{ color: "var(--ink-faint)", mt: 0.25 }}>Cadastre um novo usuário com acesso ao sistema</Typography>
        </div>
        <IconButton onClick={handleClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="create-usuario-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome completo" size="small" fullWidth sx={fieldSx}
              {...register("nome")} autoComplete="off"
              error={!!errors.nome} helperText={(errors.nome?.message as string) || "Opcional"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="E-mail*" size="small" fullWidth sx={fieldSx}
              {...register("email")} autoComplete="off" error={!!errors.email} helperText={errors.email?.message as string}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Senha*" size="small" fullWidth sx={fieldSx}
              type={showSenha ? "text" : "password"}
              {...register("senha")} autoComplete="new-password" error={!!errors.senha} helperText={errors.senha?.message as string}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowSenha((v) => !v)} sx={{ color: "var(--ink-faint)" }} aria-label={showSenha ? "Ocultar senha" : "Mostrar senha"}>
                      {showSenha ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Telefone*" size="small" fullWidth sx={fieldSx}
              value={watch("telefone") || ""}
              onChange={(e) => setValue("telefone", formatBrazilPhone(e.target.value), { shouldValidate: true })}
              error={!!errors.telefone} helperText={errors.telefone?.message as string}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Função*"
              size="small"
              fullWidth
              sx={fieldSx}
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoUsuario)}
              // Menu do MUI nasce em z-index 1300 e, sem ThemeProvider, fica
              // atras do overlay do Drawer. Mesmo ajuste ja feito no modal de
              // alterar plano do painel admin.
              SelectProps={{
                MenuProps: {
                  sx: {
                    zIndex: 1400,
                    "& .MuiPaper-root": { bgcolor: "var(--surface)", color: "var(--ink)" },
                  },
                },
              }}
            >
              {(catalogo?.funcoes ?? []).map((f) => (
                <MenuItem key={f.codigo} value={f.codigo}>{f.rotulo}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Permissões individuais
            </Typography>
            <Typography variant="caption" sx={{ color: "var(--ink-faint)" }}>
              Independentes da função — use com critério.
            </Typography>
            <FormControlLabel
              sx={{ display: "flex", mt: 1, ml: 0, color: "var(--ink)" }}
              control={
                <Switch
                  size="small"
                  checked={podeExcluirAgendamento}
                  onChange={(e) => setPodeExcluirAgendamento(e.target.checked)}
                />
              }
              label={
                <span style={{ fontSize: 13 }}>
                  Excluir agendamento{" "}
                  <span style={{ color: "var(--ink-faint)" }}>(apaga do banco, sem volta)</span>
                </span>
              }
            />
            <FormControlLabel
              sx={{ display: "flex", ml: 0, color: "var(--ink)" }}
              control={
                <Switch
                  size="small"
                  checked={podeVerLog}
                  onChange={(e) => setPodeVerLog(e.target.checked)}
                />
              }
              label={<span style={{ fontSize: 13 }}>Verificar log de atividades</span>}
            />
          </Grid>

          <Grid item xs={12}>
            <PermissoesPreview
              tipo={tipo}
              podeExcluirAgendamento={podeExcluirAgendamento}
              podeVerLog={podeVerLog}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={handleClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          Cancelar
        </MuiButton>
        <MuiButton type="submit" form="create-usuario-form" variant="contained" disabled={isLoading} disableElevation>
          {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Cadastrar"}
        </MuiButton>
      </Box>
    </Drawer>
  );
};

export default CreateUsuarioDrawer;
